import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  DynamicWorkflowNode,
  DynamicWorkflowTransition,
} from './dynamic-framework.models';
import { DynamicWorkflowGraphComponent } from './dynamic-workflow-graph.component';
import { WorkflowLayout } from './dynamic-workflow-layout';
import { validateWorkflowDraft, WorkflowIssue } from './dynamic-workflow-validation';

export interface PublishWorkflowDialogData {
  label: string;
  nodes: DynamicWorkflowNode[];
  transitions: DynamicWorkflowTransition[];
  layout?: WorkflowLayout;
  issues?: WorkflowIssue[];
  canEdit?: boolean;
}

export type PublishWorkflowDialogResult =
  | { action: 'publish'; startNodeKey: string }
  | { action: 'edit' };

@Component({
  selector: 'app-leads-v2-publish-workflow-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    DynamicWorkflowGraphComponent,
  ],
  templateUrl: './leads-v2-publish-workflow-dialog.component.html',
  styleUrls: ['./leads-v2-publish-workflow-dialog.component.css'],
})
export class LeadsV2PublishWorkflowDialogComponent implements AfterViewInit {
  @ViewChild(DynamicWorkflowGraphComponent)
  graph?: DynamicWorkflowGraphComponent;

  startNodeKey: string;
  issues: WorkflowIssue[] = [];
  errorNodeKeys = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: PublishWorkflowDialogData,
    private readonly dialogRef: MatDialogRef<
      LeadsV2PublishWorkflowDialogComponent,
      PublishWorkflowDialogResult
    >,
  ) {
    this.startNodeKey = data.nodes[0]?.key ?? '';
    this.validateSelection();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => this.graph?.zoomToFit());
  }

  get canPublish(): boolean {
    return !!this.startNodeKey && !this.issues.length;
  }

  validateSelection(): void {
    const client = validateWorkflowDraft(
      {
        label: this.data.label,
        nodes: this.data.nodes,
        transitions: this.data.transitions,
      },
      this.startNodeKey || undefined,
    );
    const distinct = new Map<string, WorkflowIssue>();
    [...(this.data.issues ?? []), ...client.issues].forEach(issue => {
      distinct.set(`${issue.nodeKey ?? ''}\0${issue.message}`, issue);
    });
    this.issues = [...distinct.values()];
    this.errorNodeKeys = new Set(
      this.issues.map(issue => issue.nodeKey).filter((key): key is string => !!key),
    );
  }

  edit(): void {
    this.dialogRef.close({ action: 'edit' });
  }

  publish(): void {
    if (!this.canPublish) return;
    this.dialogRef.close({ action: 'publish', startNodeKey: this.startNodeKey });
  }
}
